import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useGetAllTicketsQuery } from '../../store/api/ticketApi';
import { Loader } from '../../components/ui/Loader';
import type { Ticket } from '../../store/api/ticketApi';

function getStatusColor(status: string) {
  switch (status) {
    case 'open':
      return 'text-green-500 bg-green-500/10';
    case 'pending':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'closed':
      return 'text-gray-500 bg-gray-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'high':
      return 'text-red-500 bg-red-500/10';
    case 'medium':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'low':
      return 'text-blue-500 bg-blue-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

export function SupportTicketsListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: '',
    sortBy: 'newest',
    page: 1,
    limit: 20,
  });

  const { data, isLoading, error } = useGetAllTicketsQuery(filters);
  const tickets = data?.data || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-16">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/support"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('support.tickets.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('support.tickets.subtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 flex-wrap animate-scale-in">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder={t('support.tickets.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1})}
                className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1})}
              className="px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
            >
              <option value="">{t('support.tickets.allStatuses')}</option>
              <option value="open">{t('tickets.status.open')}</option>
              <option value="pending">{t('tickets.status.pending')}</option>
              <option value="closed">{t('tickets.status.closed')}</option>
            </select>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value, page: 1})}
              className="px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
            >
              <option value="">{t('support.tickets.allPriorities')}</option>
              <option value="low">{t('tickets.priority.low')}</option>
              <option value="medium">{t('tickets.priority.medium')}</option>
              <option value="high">{t('tickets.priority.high')}</option>
            </select>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1})}
              className="px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
            >
              <option value="">{t('support.tickets.allCategories')}</option>
              <option value="numbers">{t('tickets.category.numbers')}</option>
              <option value="proxies">{t('tickets.category.proxies')}</option>
              <option value="payments">{t('tickets.category.payments')}</option>
              <option value="other">{t('tickets.category.other')}</option>
            </select>
          </div>

          {/* Tickets List */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
            {tickets.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto mb-4 text-neutral-medium opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="text-xl font-heading font-semibold text-neutral-light mb-2">
                  {t('support.tickets.empty')}
                </h3>
                <p className="text-neutral-medium mb-6">{t('support.tickets.emptyDesc')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket: Ticket) => (
                  <Link
                    key={ticket._id}
                    to={`/support/tickets/${ticket._id}`}
                    className="block p-6 bg-neutral/5 rounded-2xl border border-neutral/10 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/10 transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <h3 className="text-lg font-heading font-semibold text-neutral-light truncate">
                            {ticket.title}
                          </h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                            {t(`tickets.status.${ticket.status}`)}
                          </span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                            {t(`tickets.priority.${ticket.priority}`)}
                          </span>
                        </div>
                        <p className="text-neutral-medium text-sm mb-2">
                          {t('support.tickets.user')}: {ticket.user.firstName} {ticket.user.lastName}
                          {ticket.user.email && ` (${ticket.user.email})`}
                        </p>
                        <p className="text-neutral-medium text-sm">
                          {t('support.tickets.category')}: {t(`tickets.category.${ticket.category}`)}
                        </p>
                        <p className="text-neutral-medium text-xs mt-1">
                          {formatDate(ticket.lastMessageAt || ticket.createdAt)}
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-neutral-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Back Button */}
          <div className="text-center">
            <Link
              to="/support"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
